import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { db } from '../firebase'; 
import { collection, query, where, getDocs, orderBy, deleteDoc, updateDoc, doc, serverTimestamp } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enUS } from 'date-fns/locale/en-US';
registerLocale('en-US', enUS);

// --- MODAL 1: RE-PROGRAMAR (Cambiar Hora) ---
const RescheduleModal = ({ isOpen, onClose, onConfirm, currentDate }) => {
    const [newDate, setNewDate] = useState(currentDate?.toDate() || new Date());

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[110] p-4" onClick={onClose}>
            {/* Estilos oscuros para el calendario */}
            <style>{`
                .react-datepicker { font-family: inherit; background-color: #1f2937; border-color: #374151; color: #e5e7eb; }
                .react-datepicker__header { background-color: #111827; border-bottom-color: #374151; }
                .react-datepicker__current-month, .react-datepicker-time__header { color: #f3f4f6; }
                .react-datepicker__day { color: #e5e7eb; }
                .react-datepicker__day:hover { background-color: #374151; }
                .react-datepicker__day--selected { background-color: #4f46e5 !important; color: white !important; }
                .react-datepicker__time-container .react-datepicker__time { background-color: #1f2937; color: #e5e7eb; }
                 .react-datepicker__input-container input {
                    width: 100%; padding: 0.5rem; background-color: #000; border: 1px solid #4b5563;
                    border-radius: 0.5rem; color: white; outline: none; text-align: center;
                }
            `}</style>

            <div className="bg-[#111] border border-[#333] rounded-xl shadow-2xl w-full max-w-sm p-6 text-white relative" onClick={e => e.stopPropagation()}>
                <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    Re-programar Estreno
                </h2>
                <p className="text-gray-400 text-sm mb-4">Elige la nueva fecha y hora para liberar este capítulo.</p>
                
                <div className="mb-6">
                    <DatePicker
                        selected={newDate}
                        onChange={(date) => setNewDate(date)}
                        showTimeSelect
                        timeFormat="HH:mm"
                        timeIntervals={15}
                        dateFormat="MMMM d, yyyy h:mm aa"
                        minDate={new Date()}
                        locale="en-US"
                        inline // Calendario siempre visible
                    />
                </div>

                <div className="flex justify-end space-x-3 pt-4 border-t border-[#333]">
                    <button onClick={onClose} className="px-4 py-2 font-bold text-gray-400 hover:text-white transition-colors">Cancelar</button>
                    <button 
                        onClick={() => onConfirm(newDate)} 
                        className="px-6 py-2 font-bold text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg transition-colors shadow-lg"
                    >
                        Guardar Nueva Fecha
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MODAL 2: CANCELAR ESTRENO (El "Tache") ---
const CancelReleaseModal = ({ isOpen, onClose, onRevert, onDelete, title }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[110] p-4" onClick={onClose}>
            <div className="bg-[#111] border border-red-900/50 rounded-xl shadow-2xl w-full max-w-md p-6 text-white relative animate-fadeIn" onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-3 mb-4 text-red-500">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                    <h2 className="text-xl font-bold text-white">Cancelar Estreno</h2>
                </div>
                
                <p className="text-gray-300 mb-2">
                    Estás a punto de cancelar el lanzamiento de <span className="text-white font-bold">"{title}"</span>.
                </p>
                <p className="text-gray-500 text-sm mb-6">
                    Elige qué deseas hacer con el contenido del capítulo:
                </p>

                <div className="space-y-3">
                    {/* Opción 1: Regresar a Borradores */}
                    <button 
                        onClick={onRevert}
                        className="w-full flex items-center justify-between p-4 bg-[#222] hover:bg-[#333] border border-[#444] hover:border-yellow-500/50 rounded-lg group transition-all text-left"
                    >
                        <div>
                            <span className="block font-bold text-white group-hover:text-yellow-400 transition-colors">Regresar a Borradores</span>
                            <span className="text-xs text-gray-500">El capítulo se guardará como borrador. No se perderá nada.</span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 group-hover:text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                    </button>

                    {/* Opción 2: Eliminar Permanentemente */}
                    <button 
                        onClick={onDelete}
                        className="w-full flex items-center justify-between p-4 bg-[#222] hover:bg-red-900/20 border border-[#444] hover:border-red-500/50 rounded-lg group transition-all text-left"
                    >
                        <div>
                            <span className="block font-bold text-white group-hover:text-red-400 transition-colors">Eliminar Permanentemente</span>
                            <span className="text-xs text-gray-500">Se borrará para siempre. Esta acción es irreversible.</span>
                        </div>
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 group-hover:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                </div>

                <button onClick={onClose} className="mt-6 w-full py-2 text-sm text-gray-500 hover:text-white font-bold transition-colors">
                    Cancelar (No hacer nada)
                </button>
            </div>
        </div>
    );
};

// --- MODAL 3: ELIMINAR BORRADOR SIMPLE (Ya lo tenías) ---
const DashboardDeleteModal = ({ isOpen, onConfirm, onCancel, title }) => {
    if (!isOpen) return null;
    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4" onClick={onCancel}>
            <div className="bg-[#111] border border-[#333] rounded-xl shadow-2xl w-full max-w-md p-6 text-white relative" onClick={e => e.stopPropagation()}>
                <h2 className="text-xl font-bold mb-4 text-white">¿Eliminar Borrador?</h2>
                <p className="text-gray-400 mb-6">
                    Estás a punto de eliminar <span className="text-white font-semibold">"{title}"</span>. 
                    Esta acción no se puede deshacer.
                </p>
                <div className="flex justify-end space-x-3">
                    <button onClick={onCancel} className="px-4 py-2 font-bold text-gray-300 hover:text-white bg-[#222] hover:bg-[#333] rounded-lg transition-colors">Cancelar</button>
                    <button onClick={onConfirm} className="px-4 py-2 font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors shadow-lg shadow-red-900/20">Sí, Eliminar</button>
                </div>
            </div>
        </div>
    );
};

const DashboardPro = ({ userProfile }) => {
    const [isLoadingPortal, setIsLoadingPortal] = useState(false);
    const [stats, setStats] = useState({ totalViews: 0, totalHearts: 0, totalStories: 0 });
    const [stories, setStories] = useState([]);
    const [drafts, setDrafts] = useState([]); 
    const [scheduledChapters, setScheduledChapters] = useState([]); 
    const [isLoadingData, setIsLoadingData] = useState(true);

    // Estados para Modales de Acción
    const [draftToDelete, setDraftToDelete] = useState(null); // Borrar Borrador
    const [releaseToCancel, setReleaseToCancel] = useState(null); // Cancelar Estreno (Objeto capitulo)
    const [releaseToReschedule, setReleaseToReschedule] = useState(null); // Reprogramar (Objeto capitulo)

    const location = useLocation();
    const [notification, setNotification] = useState(null);

    useEffect(() => {
        const handleFocus = () => setIsLoadingPortal(false);
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    useEffect(() => {
        if (location.state?.notification) {
            setNotification(location.state.notification);
            window.history.replaceState({}, document.title);
            const timer = setTimeout(() => setNotification(null), 3000);
            return () => clearTimeout(timer);
        }
    }, [location]);

    useEffect(() => {
        const fetchData = async () => {
            if (!userProfile) return;
            try {
                const now = new Date();
                // 1. Historias
                const qStories = query(collection(db, 'stories'), where('authorId', '==', userProfile.uid), orderBy('lastUpdatedAt', 'desc'));
                const storiesSnap = await getDocs(qStories);
                
                // 2. Capítulos para vistas
                const qAllChapters = query(collection(db, 'chapters'), where('authorId', '==', userProfile.uid));
                const allChaptersSnap = await getDocs(qAllChapters);
                const viewsByStory = {}; 
                let globalViews = 0;
                allChaptersSnap.forEach(doc => {
                    const ch = doc.data();
                    const views = ch.viewCount || 0;
                    globalViews += views;
                    if (viewsByStory[ch.storyId]) viewsByStory[ch.storyId] += views;
                    else viewsByStory[ch.storyId] = views;
                });

                // 3. Procesar Historias
                let heartsSum = 0;
                const fetchedStories = [];
                storiesSnap.forEach(doc => {
                    const data = doc.data();
                    heartsSum += (data.heartCount || 0);
                    fetchedStories.push({ id: doc.id, ...data, totalViews: viewsByStory[doc.id] || 0 });
                });

                // 4. Borradores
                const qDrafts = query(collection(db, 'chapters'), where('authorId', '==', userProfile.uid), where('status', '==', 'draft'), orderBy('lastUpdatedAt', 'desc'));
                const draftsSnap = await getDocs(qDrafts);
                const fetchedDrafts = draftsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                // 5. Programados
                const qScheduled = query(collection(db, 'chapters'), where('authorId', '==', userProfile.uid), where('scheduledAt', '>', now), orderBy('scheduledAt', 'asc'));
                const scheduledSnap = await getDocs(qScheduled);
                const fetchedScheduled = scheduledSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

                setStats({ totalViews: globalViews, totalHearts: heartsSum, totalStories: fetchedStories.length });
                setStories(fetchedStories);
                setDrafts(fetchedDrafts);
                setScheduledChapters(fetchedScheduled);
            } catch (error) {
                console.error("Error cargando dashboard:", error);
            } finally {
                setIsLoadingData(false);
            }
        };
        fetchData();
    }, [userProfile]);

    const handleManageSubscription = async () => {
        setIsLoadingPortal(true);
        try {
            const functions = getFunctions();
            const createPortal = httpsCallable(functions, 'createStripePortalSession');
            const { data } = await createPortal({ returnUrl: window.location.href });
            window.location.assign(data.url); 
        } catch (error) {
            console.error("Error portal:", error);
            alert("Error al abrir gestión.");
            setIsLoadingPortal(false);
        }
    };

    // --- ACCIONES DE GESTIÓN ---

    // 1. Eliminar Borrador (Definitivo)
    const handleDeleteDraft = async () => {
        if (!draftToDelete) return;
        try {
            await deleteDoc(doc(db, 'chapters', draftToDelete.id));
            setDrafts(prev => prev.filter(d => d.id !== draftToDelete.id));
            setDraftToDelete(null);
            setNotification({ message: "Borrador eliminado.", type: "success" });
            setTimeout(() => setNotification(null), 3000);
        } catch (error) {
            console.error(error);
            alert("Error al eliminar.");
        }
    };

    // 2. Cancelar Estreno -> Regresar a Borrador
    const handleRevertToDraft = async () => {
        if (!releaseToCancel) return;
        try {
            // Actualizamos para quitar lo programado
            await updateDoc(doc(db, 'chapters', releaseToCancel.id), {
                status: 'draft',
                scheduledAt: null,
                releaseType: null,
                lastUpdatedAt: serverTimestamp()
            });
            
            // Actualizar estados locales: Quitar de Programados, Agregar a Borradores
            setScheduledChapters(prev => prev.filter(c => c.id !== releaseToCancel.id));
            setDrafts(prev => [releaseToCancel, ...prev]); // Lo ponemos al inicio
            
            setReleaseToCancel(null);
            setNotification({ message: "Estreno cancelado. El capítulo ahora es un borrador.", type: "success" });
            setTimeout(() => setNotification(null), 3000);
        } catch (error) {
            console.error(error);
            alert("Error al revertir.");
        }
    };

    // 3. Cancelar Estreno -> Eliminar (Definitivo)
    const handleDeleteScheduled = async () => {
        if (!releaseToCancel) return;
        try {
            await deleteDoc(doc(db, 'chapters', releaseToCancel.id));
            setScheduledChapters(prev => prev.filter(c => c.id !== releaseToCancel.id));
            setReleaseToCancel(null);
            setNotification({ message: "Capítulo y estreno eliminados permanentemente.", type: "success" });
            setTimeout(() => setNotification(null), 3000);
        } catch (error) {
            console.error(error);
            alert("Error al eliminar.");
        }
    };

    // 4. Re-programar Fecha
    const handleRescheduleConfirm = async (newDate) => {
        if (!releaseToReschedule || !newDate) return;
        try {
            // Actualizar en DB
            await updateDoc(doc(db, 'chapters', releaseToReschedule.id), {
                scheduledAt: newDate,
                lastUpdatedAt: serverTimestamp()
            });

            // Actualizar localmente y reordenar
            setScheduledChapters(prev => {
                const updatedList = prev.map(item => 
                    item.id === releaseToReschedule.id 
                    ? { ...item, scheduledAt: { toDate: () => newDate } } // Mock del timestamp para que UI funcione sin recargar
                    : item
                );
                // Reordenar por fecha ascendente
                return updatedList.sort((a, b) => a.scheduledAt.toDate() - b.scheduledAt.toDate());
            });

            setReleaseToReschedule(null);
            setNotification({ message: "Fecha de estreno actualizada.", type: "success" });
            setTimeout(() => setNotification(null), 3000);
        } catch (error) {
            console.error(error);
            alert("Error al reprogramar.");
        }
    };


    if (isLoadingData) return <div className="min-h-screen bg-black flex items-center justify-center text-white">Cargando tu centro de comando...</div>;

    return (
        <div className="min-h-screen text-white p-4 sm:p-8 pb-20 relative font-sans">
            
            {/* Notificación Global */}
            {notification && (
                <div className="fixed top-24 right-8 z-50 animate-bounce-in">
                    <div className="bg-green-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center gap-3 border border-green-400">
                        <div className="bg-white rounded-full p-1">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}><path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" /></svg>
                        </div>
                        <div><h4 className="font-bold text-sm">¡Éxito!</h4><p className="text-sm text-green-100">{notification.message}</p></div>
                        <button onClick={() => setNotification(null)} className="ml-2 text-green-200 hover:text-white">✕</button>
                    </div>
                </div>
            )}
            
            {/* HEADER */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 flex flex-row justify-between items-center border-b border-[#333] pb-6 gap-4">
                <div>
                    <div className="flex items-center gap-3 mb-1">
                        <h1 className="text-3xl md:text-4xl font-bold text-white tracking-tight">Dashboard</h1>
                        <span className="bg-gradient-to-r from-[#44EFFF] to-blue-500 text-black text-xs font-black px-3 py-1 rounded uppercase tracking-wider transform -rotate-2 shadow-[0_0_10px_rgba(68,239,255,0.4)]">PRO</span>
                    </div>
                    <p className="text-gray-400">Bienvenido, <span className="text-white font-semibold">{userProfile.displayName}</span>.</p>
                </div>
                
                {/* BOTÓN DE GESTIÓN (ENGRANAJE) */}
                <button onClick={handleManageSubscription} disabled={isLoadingPortal} title="Gestionar Suscripción" className="w-10 h-10 flex items-center justify-center bg-[#111] hover:bg-[#222] border border-[#333] hover:border-[#555] rounded-lg transition-all text-gray-400 hover:text-white hover:shadow-lg">
                    {isLoadingPortal ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div> : 
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c-.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                            {/* Parte 2: El círculo central (esto es lo que faltaba) */}
                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                    }
                </button>
            </div>

            {/* GRID BENTO */}
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6 auto-rows-min">
                
                {/* 1. ESTADÍSTICAS */}
                <div className="col-span-1 md:col-span-3 lg:col-span-4 flex flex-wrap md:flex-nowrap gap-4">
                    <div className="flex-1 bg-[#111] border border-[#222] rounded-xl p-6 flex flex-col justify-center items-center md:items-start hover:border-[#333] transition-colors">
                        <p className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2">Vistas Totales</p>
                        <p className="text-4xl font-black text-white">{stats.totalViews.toLocaleString()}</p>
                    </div>
                    <div className="flex-1 bg-[#111] border border-[#222] rounded-xl p-6 flex flex-col justify-center items-center md:items-start hover:border-[#333] transition-colors">
                        <p className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2">Me Gusta</p>
                        <p className="text-4xl font-black text-pink-500">{stats.totalHearts.toLocaleString()}</p>
                    </div>
                    <div className="flex-1 bg-[#111] border border-[#222] rounded-xl p-6 flex flex-col justify-center items-center md:items-start hover:border-[#333] transition-colors">
                        <p className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2">Seguidores</p>
                        <p className="text-4xl font-black text-[#44EFFF]">{userProfile.followerCount.toLocaleString()}</p>
                    </div>
                     <div className="flex-1 bg-[#111] border border-[#222] rounded-xl p-6 flex flex-col justify-center items-center md:items-start hover:border-[#333] transition-colors">
                        <p className="text-gray-500 text-xs uppercase font-bold tracking-widest mb-2">Historias</p>
                        <p className="text-4xl font-black text-emerald-400">{stats.totalStories}</p>
                    </div>
                </div>

                {/* 2. MESA DE TRABAJO (Borradores) */}
                <div className="bg-[#111] rounded-2xl p-6 border border-[#222] col-span-1 md:col-span-2 lg:col-span-3 min-h-[400px] flex flex-col">
                    <div className="flex justify-between items-center mb-6">
                        <h2 className="text-xl font-bold flex items-center gap-2 text-white"><span>📝</span> Mesa de Trabajo <span className="text-gray-500 text-sm font-normal">(Borradores)</span></h2>
                        <Link to="/write" className="bg-[#44EFFF] hover:bg-[#33c9d6] text-black px-4 py-2 rounded-lg text-sm font-bold transition-colors shadow-[0_0_15px_rgba(68,239,255,0.2)]">+ Nueva Historia</Link>
                    </div>

                    <div className="flex-grow flex flex-col gap-3">
                        {drafts.length > 0 ? (
                            drafts.map(draft => (
                                <Link to={`/write/edit/${draft.id}`} key={draft.id} className="group block p-4 bg-black hover:bg-[#1a1a1a] rounded-xl border border-[#333] hover:border-[#555] transition-all relative">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="font-bold text-lg text-gray-200 group-hover:text-white">{draft.chapterTitle || "Capítulo sin título"}</h3>
                                            <p className="text-sm text-gray-500 mt-1">de: <span className="text-[#44EFFF]">{draft.storyTitle}</span> • Editado {draft.lastUpdatedAt ? "recientemente" : "hace tiempo"}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs bg-yellow-500/10 text-yellow-500 px-2 py-1 rounded font-mono border border-yellow-500/20">BORRADOR</span>
                                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setDraftToDelete(draft); }} className="p-2 text-gray-500 hover:text-red-500 hover:bg-red-500/10 rounded-full transition-colors z-10" title="Eliminar borrador">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                </Link>
                            ))
                        ) : (
                            <div className="flex-grow flex flex-col items-center justify-center text-gray-500 border-2 border-dashed border-[#333] rounded-xl bg-black/50">
                                <p className="mb-2 font-medium">No tienes borradores guardados en la nube.</p>
                                <p className="text-xs opacity-50 max-w-xs text-center">Los borradores locales de "auto-guardado" no aparecen aquí todavía.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* 3. ESTRENOS / PROXIMAMENTE (CON BOTONES) */}
                <div className="bg-[#111] rounded-2xl p-6 border border-[#222] col-span-1 h-full flex flex-col">
                    <h2 className="text-xl font-bold mb-4 flex items-center gap-2 text-white"><span>📅</span> Estrenos</h2>
                    
                    <div className="flex-grow flex flex-col gap-3">
                        {scheduledChapters.length > 0 ? (
                            scheduledChapters.map(chapter => (
                                <div key={chapter.id} className="relative block p-4 bg-black rounded-lg border border-[#333] hover:border-[#555] transition-colors">
                                    <div className="flex justify-between items-start mb-2">
                                        <h4 className="font-bold text-sm text-white truncate w-28">{chapter.chapterTitle}</h4>
                                        {chapter.releaseType === 'hype' ? (
                                            <span className="text-[10px] bg-pink-900/30 text-pink-400 border border-pink-800 px-1.5 py-0.5 rounded font-bold">HYPE</span>
                                        ) : (
                                            <span className="text-[10px] bg-blue-900/30 text-blue-400 border border-blue-800 px-1.5 py-0.5 rounded font-bold">SILENT</span>
                                        )}
                                    </div>
                                    <p className="text-xs text-gray-500 truncate mb-3">{chapter.storyTitle}</p>
                                    
                                    {/* BARRA DE CONTROL (Fecha + Botones) */}
                                    <div className="flex items-center justify-between bg-[#111] p-1.5 rounded border border-[#222]">
                                        <div className="flex items-center gap-2 text-xs text-[#44EFFF] font-mono pl-1">
                                            {/* Solo fecha corta para ahorrar espacio */}
                                            {chapter.scheduledAt?.toDate().toLocaleDateString()} 
                                        </div>
                                        
                                        <div className="flex gap-1">
                                            {/* Botón Reloj (Reprogramar) */}
                                            <button 
                                                onClick={() => setReleaseToReschedule(chapter)}
                                                className="p-1.5 text-gray-400 hover:text-[#44EFFF] hover:bg-[#44EFFF]/10 rounded transition-colors"
                                                title="Cambiar fecha y hora"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                            </button>
                                            {/* Botón Tache (Cancelar Estreno) */}
                                            <button 
                                                onClick={() => setReleaseToCancel(chapter)}
                                                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-500/10 rounded transition-colors"
                                                title="Cancelar estreno"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="flex-grow flex flex-col items-center justify-center p-6 text-center text-gray-600 border border-dashed border-[#333] rounded-xl bg-black/50">
                                <div className="w-10 h-10 bg-[#222] rounded-full flex items-center justify-center mb-3 text-lg opacity-50">🚀</div>
                                <p className="text-xs">No hay estrenos programados.</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* 4. GESTIÓN DE HISTORIAS PUBLICADAS */}
                <div className="bg-[#111] rounded-2xl p-6 border border-[#222] col-span-1 md:col-span-3 lg:col-span-4">
                    <h2 className="text-xl font-bold mb-6 text-white">Mis Historias Publicadas</h2>
                    {stories.length > 0 ? (
                        <div className="overflow-x-auto rounded-lg border border-[#333]">
                            <table className="w-full text-left text-gray-400 text-sm">
                                <thead className="bg-black uppercase font-bold text-xs tracking-wider text-gray-500">
                                    <tr>
                                        <th className="p-4 border-b border-[#333]">Título</th>
                                        <th className="p-4 border-b border-[#333]">Género</th>
                                        <th className="p-4 text-center border-b border-[#333]">Vistas</th>
                                        <th className="p-4 text-center border-b border-[#333]">Likes</th>
                                        <th className="p-4 text-right border-b border-[#333]">Acciones</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#222] bg-[#0e0e0e]">
                                    {stories.map(story => (
                                        <tr key={story.id} className="hover:bg-[#1a1a1a] transition-colors">
                                            <td className="p-4 font-medium text-white"><Link to={`/story/${story.id}`} className="hover:text-[#44EFFF] transition-colors">{story.title}</Link></td>
                                            <td className="p-4"><span className="bg-[#222] text-gray-300 px-2 py-1 rounded text-xs border border-[#333]">{story.genre}</span></td>
                                            <td className="p-4 text-center font-mono font-bold text-white/80">{story.totalViews ? story.totalViews.toLocaleString() : "0"}</td> 
                                            <td className="p-4 text-center font-mono text-pink-500">{story.heartCount}</td>
                                            <td className="p-4 text-right">
                                                <Link to={`/write?storyId=${story.id}`} state={{ story: story }} className="text-[#44EFFF] hover:text-white font-bold text-xs uppercase tracking-wide transition-colors">+ Cap</Link>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-16 bg-black/50 rounded-lg border border-dashed border-[#333]">
                            <p className="text-gray-500 italic">No tienes historias publicadas aún.</p>
                        </div>
                    )}
                </div>

            </div>

            {/* --- ZONA DE MODALES --- */}

            {/* 1. Eliminar Borrador */}
            <DashboardDeleteModal 
                isOpen={!!draftToDelete}
                title={draftToDelete?.chapterTitle || "Sin título"}
                onCancel={() => setDraftToDelete(null)}
                onConfirm={handleDeleteDraft}
            />

            {/* 2. Cancelar Estreno (Tache) */}
            <CancelReleaseModal 
                isOpen={!!releaseToCancel}
                title={releaseToCancel?.chapterTitle || "Capítulo"}
                onClose={() => setReleaseToCancel(null)}
                onRevert={handleRevertToDraft}
                onDelete={handleDeleteScheduled}
            />

            {/* 3. Re-programar (Reloj) */}
            <RescheduleModal 
                isOpen={!!releaseToReschedule}
                currentDate={releaseToReschedule?.scheduledAt}
                onClose={() => setReleaseToReschedule(null)}
                onConfirm={handleRescheduleConfirm}
            />

        </div>
    );
};

export default DashboardPro;